import { type FC, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useRevalidator, useRouteLoaderData } from '@remix-run/react';
import {
  CommunityPostComposer,
  type CommunityPostComposerHandle,
  type ComposerAttachablePost,
  type ComposerAuthor,
  type ComposerDestination,
  type ComposerScheduling,
  communityAnnouncementConfirmationAction,
  destinationKey,
  parseVideoUpdate,
  toVideoThumbnails,
  type VideoThumbnails,
  type VideoUpdate,
  type VideoUploadAdapter,
} from '@shared/dream-components';
import { useQueryClient } from '@tanstack/react-query';
import type { JSONContent } from '@tiptap/core';

import { useEnsureCsrfToken } from '~/context/CsrfContext';
import useIsMobile from '~/hooks/useIsMobile';
import useToast, { type ToastData, ToastStatus } from '~/hooks/useToast';
import type {
  CommunityPost,
  CommunityPostEmbedAttachable,
  CommunityQuotedPost,
  CommunitySpace,
} from '~/interfaces/community';
import type { RootLoaderData } from '~/root';
import type { loader as communityLoader } from '~/routes/community';
import { communityImageSrc } from '~/routes/community/_utils/communityImageSrc';
import type { VideoAssetPayload } from '~/services/api/community/types';
import { createCommunityConsumer } from '~/services/cable';

import { AnnouncementConfirmDialog } from '../ui/AnnouncementConfirmDialog';
import { Dialog } from '../ui/Dialog';
import { UnpinWarning } from '../ui/UnpinWarning';

import useCommunityComposerPosts from '../../_hooks/useCommunityComposerPosts';
import { invalidateCommunityFeedQueries } from '../../_hooks/useCommunityFeedActionMutation';
import { useCommunityMembership } from '../../_hooks/useCommunityMembership';
import { useCommunityPostOptimistic } from '../../_hooks/useCommunityPostOptimistic';
import { useCommunityVideoPreviewPlayback } from '../../_hooks/useCommunityVideoPlayback';
import useCreateCommunityPostMutation, {
  type CreateCommunityPostResponse,
} from '../../_hooks/useCreateCommunityPostMutation';
import useMentionSuggestions from '../../_hooks/useMentionSuggestions';
import useSearchCommunityGifs from '../../_hooks/useSearchCommunityGifs';
import { useSpaceAccess } from '../../_hooks/useSpaceAccess';
import useUpdateCommunityPostMutation, {
  type UpdateCommunityPostResponse,
} from '../../_hooks/useUpdateCommunityPostMutation';
import useUploadCommunityImage from '../../_hooks/useUploadCommunityImage';
import { insertCreatedPostIntoFeedCaches } from '../../_utils/insertCreatedPostIntoFeedCaches';
import { resolveComposerDestination } from './composerDestination';
import useAnnouncementConfirmation from './useAnnouncementConfirmation';

const parseTiptapState = (raw: unknown): JSONContent | null => {
  if (raw == null) return null;
  if (typeof raw === 'object') return raw as JSONContent;
  if (typeof raw !== 'string') return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    return typeof parsed === 'object' && parsed !== null ? (parsed as JSONContent) : null;
  } catch {
    return null;
  }
};

const editedPostDestination = (post: CommunityPost): ComposerDestination =>
  post.space
    ? {
        kind: 'channel',
        space: {
          id: post.space.id,
          slug: post.space.slug,
          name: post.space.name,
          gated: post.space.gated,
        },
      }
    : { kind: 'feed' };

const isErrorResponse = (
  data: CreateCommunityPostResponse | UpdateCommunityPostResponse,
): data is { toast: ToastData } => {
  if (typeof data !== 'object' || data === null || !('toast' in data)) return false;
  return data.toast !== undefined && data.toast.status === ToastStatus.ERROR;
};

/** A post kept elsewhere that the composer edits, like a scheduled post, in the fields of a post. */
export type ComposerInitialDraft = Pick<CommunityPost, 'tiptap_state' | 'attachable' | 'author'> & {
  title: string | null;
  space_slug: string | null;
};

/** Sending at a publish time, with panels that take turns over the hidden composer. */
export interface PostComposerScheduling {
  /** The clock and the chip of the chosen time. */
  composer?: ComposerScheduling;
  isHidden: boolean;
  isPending: boolean;
  error: string | null;
  /** Whether the picker's send was refused, which shows in a toast and closes the announcement confirmation. */
  isRefused: boolean;
  validate: () => boolean;
  /** Sends the draft at the time picked for it, or at the edited post's time, and says whether it did. */
  submit: (draft: { payload: Record<string, string>; destination: ComposerDestination; scheduledAt?: Date }) => boolean;
  /** Rendered inside the composer's dialog, so the focus and the clicks stay inside it. */
  renderDialogs: (composer: {
    hasDraft: () => boolean;
    /** Sends the draft from the picker, at the time picked there. */
    schedule: (scheduledAt: Date) => void;
    isSubmitting: boolean;
  }) => ReactNode;
}

interface PostComposerModalProps {
  isOpen: boolean;
  onClose: () => void;
  spaces: CommunitySpace[];
  defaultSpaceSlug?: string;
  quotedPost?: CommunityPost;
  editPost?: CommunityPost;
  initialDraft?: ComposerInitialDraft;
  scheduling?: PostComposerScheduling;
  /** Skips the dialog's entrance, for a composer that replaces another in the open dialog, so the backdrop stays. */
  disableInitialTransition?: boolean;
}

export const PostComposerModal: FC<PostComposerModalProps> = ({
  isOpen,
  onClose,
  spaces,
  defaultSpaceSlug,
  quotedPost,
  editPost,
  initialDraft,
  scheduling,
  disableInitialTransition,
}) => {
  const { t, i18n } = useTranslation();
  const isEditMode = Boolean(editPost || initialDraft);
  const ensureCsrfToken = useEnsureCsrfToken();
  const isMobile = useIsMobile();
  const revalidator = useRevalidator();
  const queryClient = useQueryClient();
  const createPostMutation = useCreateCommunityPostMutation();
  const updatePostMutation = useUpdateCommunityPostMutation();
  const uploadImageMutation = useUploadCommunityImage();
  const searchCommunityGifs = useSearchCommunityGifs();
  const { applyOptimisticPatch, clearOptimisticPatch } = useCommunityPostOptimistic();
  const { canPostIn } = useSpaceAccess();
  const { currentMember, permissions } = useCommunityMembership();
  const communityLayoutData = useRouteLoaderData<typeof communityLoader>('routes/community');
  const feedFirstEnabled = communityLayoutData?.community?.feed_first_enabled === true;

  const resolvedDestination = useMemo(
    () =>
      resolveComposerDestination({
        feedFirstEnabled,
        canCreatePost: permissions.canCreatePost,
        canMovePost: permissions.canMoveAnyPost,
        spaces,
        canPostIn,
        defaultSpaceSlug,
        editPost,
        initialDraft,
      }),
    [
      feedFirstEnabled,
      permissions.canCreatePost,
      permissions.canMoveAnyPost,
      spaces,
      canPostIn,
      defaultSpaceSlug,
      editPost,
      initialDraft,
    ],
  );

  const [selectedDestination, setSelectedDestination] = useState<ComposerDestination | null>(null);
  const destination = resolvedDestination ? (selectedDestination ?? resolvedDestination.destination) : null;
  const moveDestination =
    editPost && destination && destinationKey(destination) !== destinationKey(editedPostDestination(editPost))
      ? destination
      : null;
  const [title, setTitle] = useState(() => editPost?.title ?? initialDraft?.title ?? '');
  const [error, setError] = useState<string | null>(null);
  const shownError = error ?? scheduling?.error ?? null;
  const [toastData, setToastData] = useState<ToastData>();
  const composerRef = useRef<CommunityPostComposerHandle>(null);

  const editedAttachable = editPost?.attachable ?? initialDraft?.attachable;
  const [isDraftQuoteRemoved, setIsDraftQuoteRemoved] = useState(false);
  const displayQuotedPost: CommunityQuotedPost | undefined =
    quotedPost ?? (editedAttachable?.type === 'quoted_post' && !isDraftQuoteRemoved ? editedAttachable : undefined);

  const displayPostEmbed: CommunityPostEmbedAttachable | undefined =
    editedAttachable?.type === 'post' ? editedAttachable : undefined;

  const [attachedPosts, setAttachedPosts] = useState<ComposerAttachablePost[]>(() => {
    if (!displayPostEmbed) return [];
    return [
      {
        id: displayPostEmbed.id,
        web_title: displayPostEmbed.web_title,
        web_subtitle: displayPostEmbed.web_subtitle,
        image_url: displayPostEmbed.image_url,
        publication_logo_url: displayPostEmbed.publication_logo_url,
        publication_via_label: t('community.post_embed_card.via', { name: displayPostEmbed.publication_name }),
        authors: displayPostEmbed.authors.map((a) => ({ name: a.name, avatar_url: a.profile_picture?.url ?? null })),
      },
    ];
  });

  // An opened draft only counts as one to lose once the member changes it.
  const [isDraftChanged, setIsDraftChanged] = useState(false);
  const hasDraft = () =>
    initialDraft
      ? isDraftChanged
      : Boolean(title.trim() || quotedPost || attachedPosts.length > 0) || !(composerRef.current?.isEmpty() ?? true);

  const { setQuery: setPostQuery, ...postsQuery } = useCommunityComposerPosts({ enabled: isOpen && !isEditMode });

  const rootData = useRouteLoaderData<RootLoaderData>('root');
  const composerPosts = useMemo<ComposerAttachablePost[]>(
    () =>
      (postsQuery.data?.posts ?? []).map((p) => ({
        id: p.id,
        web_title: p.web_title,
        web_subtitle: p.web_subtitle,
        image_url: p.image_url,
        // The site's own posts carry no publication branding in the loader payload;
        // they all belong to this site's publication, so stamp it from the root data.
        publication_logo_url: rootData?.site?.publication?.logo?.url ?? null,
        publication_via_label: rootData?.site?.publication?.name
          ? t('community.post_embed_card.via', { name: rootData.site.publication.name })
          : null,
        authors: p.authors.map((a) => ({ name: a.name, avatar_url: a.profile_picture?.url ?? null })),
        scheduled_at: p.override_scheduled_at,
      })),
    [postsQuery.data, rootData?.site?.publication, t],
  );

  const editedTiptapState = editPost?.tiptap_state ?? initialDraft?.tiptap_state;
  const initialEditorContent = useMemo(
    () => (editedTiptapState ? parseTiptapState(editedTiptapState) : null),
    [editedTiptapState],
  );
  const placeholder = t('community.composer.body_placeholder');

  const resetForm = useCallback(() => {
    setSelectedDestination(null);
    setTitle('');
    setError(null);
    setAttachedPosts([]);
    setPostQuery('');
    composerRef.current?.reset();
  }, [setPostQuery]);

  useToast(toastData);

  const handleCreateSuccess = useCallback(
    (data: CreateCommunityPostResponse) => {
      if (isErrorResponse(data)) {
        setError(data.toast.message ?? t('community.composer.error'));
        return;
      }
      if (!('post' in data)) {
        setError(t('community.composer.error'));
        return;
      }

      // Only the for_you caches: the author can't appear in their own following feed.
      insertCreatedPostIntoFeedCaches(queryClient, data.post);

      if (data.toast?.status === ToastStatus.SUCCESS) {
        setToastData(data.toast);
      }

      resetForm();
      onClose();
    },
    [onClose, queryClient, resetForm, t],
  );

  const handleCreateError = useCallback(() => {
    setError(t('community.composer.error'));
  }, [t]);

  const handleUpdateSuccess = useCallback(
    (data: UpdateCommunityPostResponse) => {
      if (!editPost) return;

      if (isErrorResponse(data)) {
        clearOptimisticPatch(editPost.id);
        setError(data.toast.message ?? t('community.composer.error'));
        return;
      }
      if (!('id' in data)) {
        clearOptimisticPatch(editPost.id);
        setError(t('community.composer.error'));
        return;
      }

      const patch: Partial<CommunityPost> = {
        title: data.title,
        updated_at: data.updated_at ?? editPost.updated_at,
        edited_at: data.edited_at ?? editPost.edited_at,
      };
      if ('tiptap_state' in data && data.tiptap_state !== undefined) {
        patch.tiptap_state = data.tiptap_state;
      }
      applyOptimisticPatch(editPost.id, patch);
      if (moveDestination) {
        invalidateCommunityFeedQueries(queryClient);
      }

      if (data.toast?.status === ToastStatus.SUCCESS) {
        setToastData(data.toast);
      }

      revalidator.revalidate();
      resetForm();
      onClose();
    },
    [
      applyOptimisticPatch,
      clearOptimisticPatch,
      editPost,
      moveDestination,
      onClose,
      queryClient,
      resetForm,
      revalidator,
      t,
    ],
  );

  const handleUpdateError = useCallback(() => {
    if (editPost) {
      clearOptimisticPatch(editPost.id);
    }
    setError(t('community.composer.error'));
  }, [clearOptimisticPatch, editPost, t]);

  const submitPost = useCallback(
    (contentJson: JSONContent, scheduledAt?: Date) => {
      const attachedPost = attachedPosts[0];

      if (editPost) {
        applyOptimisticPatch(editPost.id, {
          title,
          tiptap_state: contentJson,
        });
        const updatePayload: Record<string, string> = {
          post_id: editPost.id,
          title,
          content_json: JSON.stringify(contentJson),
        };
        if (moveDestination) {
          updatePayload.destination_kind = moveDestination.kind;
        }
        if (moveDestination?.kind === 'channel') {
          updatePayload.destination_space_slug = moveDestination.space.slug;
        }

        updatePostMutation.mutate(updatePayload, {
          onSuccess: handleUpdateSuccess,
          onError: handleUpdateError,
        });
        return;
      }

      if (!destination) return;

      const submitPayload: Record<string, string> = {
        title,
        content_json: JSON.stringify(contentJson),
      };
      if (destination.kind === 'channel') {
        submitPayload.space_slug = destination.space.slug;
      }

      if (displayQuotedPost?.id) {
        submitPayload.attachable_type = 'CommunityPost';
        submitPayload.attachable_id = displayQuotedPost.id;
      } else if (attachedPost) {
        submitPayload.attachable_type = 'Post';
        submitPayload.attachable_id = attachedPost.id;
      }

      if (scheduling?.submit({ payload: submitPayload, destination, scheduledAt })) return;

      createPostMutation.mutate(
        { ...submitPayload, composer_mode: quotedPost ? 'quote' : 'default' },
        {
          onSuccess: handleCreateSuccess,
          onError: handleCreateError,
        },
      );
    },
    [
      attachedPosts,
      displayQuotedPost,
      quotedPost,
      editPost,
      destination,
      moveDestination,
      title,
      applyOptimisticPatch,
      createPostMutation,
      handleCreateError,
      handleCreateSuccess,
      handleUpdateError,
      handleUpdateSuccess,
      scheduling,
      updatePostMutation.mutate,
    ],
  );

  const showAnnouncementError = useCallback(() => setError(t('community.composer.error')), [t]);
  const { isChecking, isScheduling, submitWithConfirmation, dialogProps } = useAnnouncementConfirmation({
    destination,
    editPostId: editPost?.id ?? null,
    submitPost,
    onError: showAnnouncementError,
    hasSubmitError: Boolean(shownError || scheduling?.isRefused),
  });

  const isSubmitting =
    createPostMutation.isPending || updatePostMutation.isPending || Boolean(scheduling?.isPending) || isChecking;

  const handleSubmit = useCallback(
    async (contentJson: JSONContent, scheduledAt?: Date) => {
      if (!title.trim() && !displayQuotedPost) {
        setToastData({ status: ToastStatus.ERROR, message: t('community.composer.error_title_required') });
        return;
      }

      const isEmpty = composerRef.current?.isEmpty() ?? true;
      const hasAttachedPost = attachedPosts.length > 0;
      if (isEmpty && !displayQuotedPost && !hasAttachedPost) {
        setError(t('community.composer.error_empty'));
        return;
      }

      setError(null);
      if (scheduling && !scheduling.validate()) return;

      await submitWithConfirmation(contentJson, scheduledAt);
    },
    [attachedPosts, displayQuotedPost, scheduling, submitWithConfirmation, t, title],
  );

  const handleUploadImage = useCallback(
    async (file: File): Promise<{ url: string; alt?: string; blobKey: string } | null> => {
      const data = await uploadImageMutation.mutateAsync(file);
      return { url: data.public_url, alt: file.name, blobKey: data.key };
    },
    [uploadImageMutation],
  );

  // An opened draft's video belongs to its author, so another member editing it neither adds nor works on videos.
  const viewerIsAuthor = !initialDraft || initialDraft.author.id === currentMember?.id;

  const videoEnabled = Boolean(
    communityLayoutData?.community?.video_enabled &&
      communityLayoutData?.currentMember?.can_upload_video &&
      viewerIsAuthor,
  );
  const mentionSuggestions = useMentionSuggestions({
    spaceSlug: destination?.kind === 'channel' ? destination.space.slug : undefined,
    context: 'post',
  });

  const videoUploadAdapter = useMemo<VideoUploadAdapter>(() => {
    const send = async <Response,>(path: string, options: RequestInit): Promise<Response | null> => {
      const csrfToken = await ensureCsrfToken();
      const response = await fetch(path, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
        },
      });
      return response.ok ? response.json() : null;
    };

    return {
      request: (file, { multipart, signal }) =>
        send('/community_video_upload', { method: 'POST', body: JSON.stringify({ file, multipart }), signal }),
      signPart: (videoId, { signal, ...body }) =>
        send('/community_video_multipart', { method: 'POST', body: JSON.stringify({ videoId, ...body }), signal }),
      listParts: (videoId, { upload_id, signal }) =>
        send(`/community_video_multipart?${new URLSearchParams({ videoId, upload_id })}`, { method: 'GET', signal }),
      complete: (videoId, { signal, ...body }) =>
        send('/community_video_multipart', { method: 'PATCH', body: JSON.stringify({ videoId, ...body }), signal }),
      abort: async (videoId, { signal, ...body }) => {
        // The bucket lifecycle reclaims orphaned sessions if cleanup cannot reach S3.
        await send('/community_video_multipart', {
          method: 'DELETE',
          body: JSON.stringify({ videoId, ...body }),
          signal,
        }).catch(() => {});
      },
      confirm: (videoId) => send('/community_video_confirm', { method: 'POST', body: JSON.stringify({ videoId }) }),
    };
  }, [ensureCsrfToken]);

  const handleRetryVideoProcessing = useCallback(
    async (videoId: string): Promise<void> => {
      try {
        const csrfToken = await ensureCsrfToken();
        await fetch('/community_video_retry', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
          },
          body: JSON.stringify({ videoId }),
        });
      } catch {
        // Leave the card on its failed state; the subscriber can retry again.
      }
    },
    [ensureCsrfToken],
  );

  const requestVideoThumbnails = useCallback(
    async (body: FormData | Record<string, unknown>): Promise<VideoThumbnails | null> => {
      const isFormData = body instanceof FormData;
      try {
        const csrfToken = await ensureCsrfToken();
        const response = await fetch('/community_video_thumbnail', {
          method: 'POST',
          headers: {
            ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
            ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
          },
          body: isFormData ? body : JSON.stringify(body),
        });
        if (!response.ok) return null;
        const data = (await response.json()) as { video?: VideoAssetPayload };
        return data.video ? toVideoThumbnails(data.video) : null;
      } catch {
        return null;
      }
    },
    [ensureCsrfToken],
  );

  const loadVideoThumbnails = useCallback(
    (videoId: string) => requestVideoThumbnails({ videoId }),
    [requestVideoThumbnails],
  );

  const selectVideoThumbnail = useCallback(
    (videoId: string, index: number) => requestVideoThumbnails({ videoId, index }),
    [requestVideoThumbnails],
  );

  const uploadVideoThumbnail = useCallback(
    (videoId: string, file: File) => {
      const formData = new FormData();
      formData.append('videoId', videoId);
      formData.append('thumbnail', file);
      return requestVideoThumbnails(formData);
    },
    [requestVideoThumbnails],
  );

  // One consumer for the modal; each placed video opens its own channel on it and
  // the channel transmits the current snapshot on subscribe, then pushes updates.
  const videoStatusConsumer = useMemo(() => (videoEnabled ? createCommunityConsumer() : null), [videoEnabled]);
  useEffect(() => () => videoStatusConsumer?.disconnect(), [videoStatusConsumer]);

  const resolveVideoPlayback = useCommunityVideoPreviewPlayback();

  const subscribeToVideoStatus = useCallback(
    (videoId: string, onUpdate: (update: VideoUpdate) => void): (() => void) => {
      if (!videoStatusConsumer) return () => {};

      const subscription = videoStatusConsumer.subscriptions.create(
        { channel: 'OrchidCable::CommunityVideoChannel', video_id: videoId },
        {
          received: (data: unknown) => {
            const update = parseVideoUpdate(data);
            if (update) onUpdate(update);
          },
        },
      );

      return () => subscription.unsubscribe();
    },
    [videoStatusConsumer],
  );

  const handleFetchLinkPreview = useCallback(
    async (url: string) => {
      try {
        const csrfToken = await ensureCsrfToken();
        const response = await fetch('/community_link_preview', {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
            ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
          },
          body: new URLSearchParams({ url }),
        });
        const data: unknown = await response.json();
        if (typeof data !== 'object' || data === null) return null;
        const meta = data as { title?: string; description?: string; image?: string; site_name?: string };
        return {
          title: meta.title ?? undefined,
          description: meta.description ?? undefined,
          image: meta.image ?? undefined,
          siteName: meta.site_name ?? undefined,
        };
      } catch {
        return null;
      }
    },
    [ensureCsrfToken],
  );

  const translations = useMemo<Record<string, string>>(
    () => ({
      'community.composer.close': t('community.composer.close'),
      'community.composer.destination_feed': t('community.composer.destination_feed'),
      'community.composer.choose_destination': t('community.composer.choose_destination'),
      'community.action_menu.move_post_read_only_hint': t('community.action_menu.move_post_read_only_hint'),
      'community.composer.post_title_placeholder': t('community.composer.post_title_placeholder'),
      'community.composer.submit': t('community.composer.submit'),
      'community.composer.save': t('community.composer.save'),
      'community.composer.submitting': t('community.composer.submitting'),
      'community.composer.saving': t('community.composer.saving'),
      'community.composer.schedule.open': t('community.composer.schedule.open'),
      'community.composer.format_text': t('community.composer.format_text'),
      'community.composer.format_bold': t('community.composer.format_bold'),
      'community.composer.format_italic': t('community.composer.format_italic'),
      'community.composer.format_underline': t('community.composer.format_underline'),
      'community.composer.format_strike': t('community.composer.format_strike'),
      'community.composer.format_code': t('community.composer.format_code'),
      'community.composer.add_media': t('community.composer.add_media'),
      'community.composer.media_tab_gif': t('community.composer.media_tab_gif'),
      'community.composer.media_tab_upload': t('community.composer.media_tab_upload'),
      'community.composer.gif_search_placeholder': t('community.composer.gif_search_placeholder'),
      'community.composer.gif_loading': t('community.composer.gif_loading'),
      'community.composer.gif_error': t('community.composer.gif_error'),
      'community.composer.gif_no_results': t('community.composer.gif_no_results'),
      'community.composer.gif_powered_by': t('community.composer.gif_powered_by'),
      'community.composer.upload_loading': t('community.composer.upload_loading'),
      'community.composer.upload_choose_file': t('community.composer.upload_choose_file'),
      'community.composer.upload_hint': t('community.composer.upload_hint'),
      'community.composer.upload_error': t('community.composer.upload_error'),
      'community.composer.add_link': t('community.composer.add_link'),
      'community.composer.link_url_placeholder': t('community.composer.link_url_placeholder'),
      'community.composer.link_save': t('community.composer.link_save'),
      'community.composer.link_invalid': t('community.composer.link_invalid'),
      'community.composer.link_remove': t('community.composer.link_remove'),
      'community.composer.add_emoji': t('community.composer.add_emoji'),
      'community.composer.emoji_search_placeholder': t('community.composer.emoji_search_placeholder'),
      'community.composer.emoji_no_results': t('community.composer.emoji_no_results'),
      'community.composer.attach_post': t('community.composer.attach_post'),
      'community.composer.post_search_placeholder': t('community.composer.post_search_placeholder'),
      'community.composer.post_loading': t('community.composer.post_loading'),
      'community.composer.post_no_results': t('community.composer.post_no_results'),
      'community.composer.post_max_reached': t('community.composer.post_max_reached'),
      'community.composer.attached_post_remove': t('community.composer.attached_post_remove'),
      'community.composer.link_preview_loading': t('community.composer.link_preview_loading'),
      'community.composer.link_preview_remove': t('community.composer.link_preview_remove'),
      'community.composer.embed_prompt_paste_as': t('community.composer.embed_prompt_paste_as'),
      'community.composer.embed_prompt_preview': t('community.composer.embed_prompt_preview'),
      'community.composer.embed_prompt_both': t('community.composer.embed_prompt_both'),
      'community.composer.embed_prompt_keep_link': t('community.composer.embed_prompt_keep_link'),
      'community.composer.add_video': t('community.composer.add_video'),
      'video.choose_file': t('video.choose_file'),
      'video.hint': t('video.hint'),
      'video.uploading': t('video.uploading'),
      'video.processing': t('video.processing'),
      'video.cancel': t('video.cancel'),
      'video.retry': t('video.retry'),
      'video.remove': t('video.remove'),
      'video.play': t('video.play'),
      'video.close': t('video.close'),
      'video.choose_thumbnail': t('video.choose_thumbnail'),
      'video.thumbnail_dialog': t('video.thumbnail_dialog'),
      'video.thumbnail_frame': t('video.thumbnail_frame'),
      'video.upload_thumbnail': t('video.upload_thumbnail'),
      'video.thumbnail_error': t('video.thumbnail_error'),
      'video.error_unsupported': t('video.error_unsupported'),
      'video.error_empty': t('video.error_empty'),
      'video.error_too_large': t('video.error_too_large'),
      'video.error_too_long': t('video.error_too_long'),
      'video.error_transfer': t('video.error_transfer'),
      'video.error_completion': t('video.error_completion'),
      'video.status_processing': t('video.status_processing'),
      'video.status_ready': t('video.status_ready'),
      'video.status_ready_to_publish': t('video.status_ready_to_publish'),
      'video.status_failed': t('video.status_failed'),
      'video.status_blocked': t('video.status_blocked'),
      'video.failed_too_long': t('video.failed_too_long'),
      'video.failed_resolution_too_high': t('video.failed_resolution_too_high'),
      'video.failed_unreadable': t('video.failed_unreadable'),
      'video.remove_to_post': t('video.remove_to_post'),
      'video.unavailable': t('video.unavailable'),
      'community.join.former_member': t('community.join.former_member'),
      'community.mentions.list_label': t('community.mentions.list_label'),
    }),
    [t],
  );

  const memberName = currentMember?.subscription
    ? `${currentMember.subscription.first_name} ${currentMember.subscription.last_name}`.trim()
    : '';
  const author: ComposerAuthor = initialDraft
    ? { name: initialDraft.author.name ?? t('community.join.former_member'), avatarUrl: initialDraft.author.avatar_url }
    : { name: memberName, avatarUrl: currentMember?.subscription?.avatar_url };

  const composerAuthor = feedFirstEnabled && author.name ? author : undefined;

  if (!resolvedDestination) return null;

  const quotedPostForPanel = displayQuotedPost
    ? {
        id: displayQuotedPost.id,
        title: displayQuotedPost.title ?? null,
        author: { name: displayQuotedPost.author.name, avatar_url: displayQuotedPost.author.avatar_url },
        space: displayQuotedPost.space
          ? {
              name: displayQuotedPost.space.name,
              emoji: displayQuotedPost.space.emoji,
              slug: displayQuotedPost.space.slug,
            }
          : null,
        created_at: displayQuotedPost.created_at,
      }
    : undefined;

  const composerContent = (
    <CommunityPostComposer
      ref={composerRef}
      initialContent={initialEditorContent}
      placeholder={placeholder}
      destinations={resolvedDestination.destinations}
      selectedDestination={selectedDestination ?? resolvedDestination.destination}
      onDestinationChange={(next) => {
        setSelectedDestination(next);
        setIsDraftChanged(true);
      }}
      destinationMode={resolvedDestination.mode}
      author={composerAuthor}
      title={title}
      onTitleChange={(next) => {
        setTitle(next);
        setIsDraftChanged(true);
      }}
      onContentChange={() => setIsDraftChanged(true)}
      isEditMode={isEditMode}
      quotedPost={quotedPostForPanel}
      // An opened draft can drop its quote. A new quote and a published post keep theirs.
      onQuotedPostRemove={
        initialDraft
          ? () => {
              setIsDraftQuoteRemoved(true);
              setIsDraftChanged(true);
            }
          : undefined
      }
      onSubmit={handleSubmit}
      isSubmitting={isSubmitting}
      isSubmitDisabled={!title.trim() && !displayQuotedPost}
      scheduling={scheduling?.composer}
      isHidden={scheduling?.isHidden}
      onClose={onClose}
      onUploadImage={handleUploadImage}
      isMediaUploadDisabled={false}
      videoUploadAdapter={videoEnabled ? videoUploadAdapter : undefined}
      subscribeToVideoStatus={subscribeToVideoStatus}
      onRetryVideoProcessing={handleRetryVideoProcessing}
      resolveVideoPlayback={resolveVideoPlayback}
      loadVideoThumbnails={videoEnabled ? loadVideoThumbnails : undefined}
      selectVideoThumbnail={videoEnabled ? selectVideoThumbnail : undefined}
      uploadVideoThumbnail={videoEnabled ? uploadVideoThumbnail : undefined}
      onSearchGifs={searchCommunityGifs}
      onFetchLinkPreview={handleFetchLinkPreview}
      mentionSuggestions={mentionSuggestions}
      attached={{ posts: attachedPosts }}
      transformImageUrl={communityImageSrc}
      // An opened draft can drop its attached post but not pick another. A published post keeps its own.
      onAttachedChange={
        editPost
          ? undefined
          : (next) => {
              setAttachedPosts(next.posts ?? []);
              setIsDraftChanged(true);
            }
      }
      {...(isEditMode
        ? {}
        : {
            attachableLists: { posts: composerPosts },
            onPostQueryChange: setPostQuery,
            isLoadingPosts: postsQuery.isFetching,
          })}
      error={shownError}
      notice={editPost?.is_pinned && moveDestination ? <UnpinWarning /> : null}
      translations={translations}
      locale={i18n.language}
      autoFocusTitle={!isMobile}
    />
  );

  // Inside the composer dialog, so a click on it doesn't count as a click outside the composer.
  const announcementConfirmDialog = (
    <AnnouncementConfirmDialog
      {...dialogProps}
      channelName={destination?.kind === 'channel' ? destination.space.name : null}
      confirmAction={communityAnnouncementConfirmationAction({
        isEditMode,
        isScheduling,
      })}
    />
  );

  // The scheduling panels take the composer's place in the same dialog. The composer stays mounted, hidden, so the
  // draft stays.
  const schedulingDialogs = scheduling?.renderDialogs({
    hasDraft,
    schedule: (scheduledAt) => {
      if (composerRef.current) handleSubmit(composerRef.current.getContent(), scheduledAt);
    },
    isSubmitting,
  });

  // One dialog for both layouts, so rotating a phone across the breakpoint doesn't remount the editor and lose the draft.
  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      position={isMobile ? 'bottom' : 'center'}
      className={
        isMobile
          ? 'flex flex-col w-full max-h-[90dvh] min-h-0 rounded-t-3xl bg-white pb-[env(safe-area-inset-bottom)]'
          : 'flex flex-col w-full max-w-[600px] min-h-[520px] max-h-[80vh] rounded-[24px] bg-white border border-black/10 shadow-[0px_8px_40px_0px_rgba(0,0,0,0.15)]'
      }
      disableInitialTransition={isMobile || disableInitialTransition}
    >
      {composerContent}
      {schedulingDialogs}
      {announcementConfirmDialog}
    </Dialog>
  );
};
